import { LOCAL_STORAGE_KEYS } from 'consts/localStorage';

/**
 * An OAuth connect should complete well within this window; an older marker means
 * the connect was cancelled/abandoned, so it is treated as stale and ignored.
 */
const PENDING_CONNECT_TTL_MS = 10 * 60 * 1000;

type PendingConnect = { integration: string; ts: number };

/**
 * Records that an integration's connect flow has started (called before the OAuth
 * tab opens) so the post-connect redirect can auto-enable its research preview.
 * Shared across tabs via localStorage so it survives the OAuth round-trip.
 *
 * @param integration - The integration id whose connect flow is starting.
 */
export const setPendingResearchPreviewConnect = (integration: string): void => {
  const marker: PendingConnect = { integration, ts: Date.now() };

  localStorage.setItem(
    LOCAL_STORAGE_KEYS.PENDING_RESEARCH_PREVIEW_CONNECT,
    JSON.stringify(marker),
  );
};

/**
 * Returns the pending integration when a fresh, well-formed marker exists. Stale
 * or malformed markers are cleared and treated as absent. A valid marker is left
 * in place — callers consume it explicitly via {@link clearPendingResearchPreviewConnect}
 * once they have acted on it.
 *
 * @returns The pending integration id, or undefined when no fresh marker exists.
 */
export const getPendingResearchPreviewConnect = (): string | undefined => {
  const raw = localStorage.getItem(
    LOCAL_STORAGE_KEYS.PENDING_RESEARCH_PREVIEW_CONNECT,
  );

  if (!raw) {
    return undefined;
  }

  let marker: PendingConnect | undefined;

  try {
    marker = JSON.parse(raw) as PendingConnect;
  } catch {
    marker = undefined;
  }

  const isStale =
    !marker ||
    typeof marker.integration !== 'string' ||
    typeof marker.ts !== 'number' ||
    Date.now() - marker.ts >= PENDING_CONNECT_TTL_MS;

  if (!marker || isStale) {
    clearPendingResearchPreviewConnect();

    return undefined;
  }

  return marker.integration;
};

/** Removes the pending-connect marker once it has been handled. */
export const clearPendingResearchPreviewConnect = (): void => {
  localStorage.removeItem(LOCAL_STORAGE_KEYS.PENDING_RESEARCH_PREVIEW_CONNECT);
};
